import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Box, Button, Switch, FormControlLabel, Typography, Chip, Stack, FormHelperText, Divider, } from '@mui/material';

const toUrl = (val, cdnBaseUrl) => {
  if (!val) return '';
  if (typeof val !== 'string') return '';
  if (val.startsWith('http')) return val;
  if (!cdnBaseUrl) return val;
  return `${cdnBaseUrl.replace(/\/$/, '')}/${val.replace(/^\//, '')}`;
};

const statusChip = ({ hasExisting, hasDraft, isRemoved }) => {
  if (isRemoved) return <Chip size="small" label="will delete" color="error" variant="filled" />;
  if (hasDraft) return <Chip size="small" label="selected" color="info" variant="filled" />;
  if (hasExisting) return <Chip size="small" label="uploaded" color="success" variant="filled" />;
  return <Chip size="small" label="no file" variant="outlined" />;
};

const OnDemandArtworkModal = ({ open, onClose, variantKey, placements, existingArtworks, cdnBaseUrl, onApply, }) => {
  const [draft, setDraft] = useState({});
  const [remove, setRemove] = useState({});

  useEffect(() => {
    if (open) {
      setDraft({});
      setRemove({});
    }
  }, [open]);

  const handleFileChange = (placement) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setRemove((prev) => {
      const copy = { ...prev };
      delete copy[placement];
      return copy;
    });

    setDraft((prev) => ({ ...prev, [placement]: file }));
  };

  const handleToggle = (placement) => (e) => {
    const nextChecked = e.target.checked;
    const hasExisting = !!existingArtworks?.[placement];
    const hasDraft = !!draft?.[placement];

    if (!nextChecked) {
      if (hasDraft) {
        setDraft((prev) => {
          const copy = { ...prev };
          delete copy[placement];
          return copy;
        });
        return;
      }

      if (hasExisting) {
        const ok = window.confirm('Remove this artwork? This will delete the file.');
        if (!ok) return;
        setRemove((prev) => ({ ...prev, [placement]: true }));
      }
      return;
    }

    setRemove((prev) => {
      const copy = { ...prev };
      delete copy[placement];
      return copy;
    });
  };

  const removePlacements = useMemo(
    () => Object.keys(remove).filter((p) => remove[p]),
    [remove]
  );

  const handleSave = () => {
    onApply({ draft, removePlacements });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6">On-Demand Artwork</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Toggle ON to keep / upload. Toggle OFF to remove (asks confirmation).
            </Typography>
          </Box>

          <Chip
            size="small"
            label={`${Object.keys(existingArtworks || {}).length} uploaded`}
            color="success"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent
        dividers
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          background: '#fafafa',
        }}
      >
        <Grid container spacing={2}>
          {placements.map((p) => {
            const hasExisting = !!existingArtworks?.[p];
            const hasDraft = !!draft?.[p];
            const isRemoved = !!remove?.[p];

            const enabled = hasDraft || (hasExisting && !isRemoved);

            const switchSx = hasExisting && enabled
              ? {
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#111' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#111' },
                  '& .MuiSwitch-track': { backgroundColor: '#d0d0d0' }
                }
              : {};

            return (
              <Grid item xs={12} sm={6} key={p}>
                <Box
                  sx={{
                    border: '1px solid #eaeaea',
                    borderRadius: 2,
                    p: 2,
                    background: 'white',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{p}</Typography>
                    {statusChip({ hasExisting, hasDraft, isRemoved })}
                  </Stack>

                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={!!enabled}
                        onChange={handleToggle(p)}
                        sx={switchSx}
                      />
                    }
                    label={enabled ? 'enabled' : 'disabled'}
                  />

                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`od-${variantKey}-${p}`}
                    onChange={handleFileChange(p)}
                  />

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                    <label htmlFor={`od-${variantKey}-${p}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        disabled={!enabled}
                      >
                        upload
                      </Button>
                    </label>

                    {hasDraft && (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={() => {
                          setDraft((prev) => {
                            const copy = { ...prev };
                            delete copy[p];
                            return copy;
                          });
                        }}
                      >
                        clear
                      </Button>
                    )}
                  </Box>

                  <FormHelperText sx={{ mt: 1 }}>
                    {isRemoved
                      ? 'Marked for deletion'
                      : hasDraft
                        ? `Selected: ${draft[p].name}`
                        : hasExisting
                          ? 'Already uploaded'
                          : 'No file'}
                  </FormHelperText>

                  {hasDraft && (
                    <a href={URL.createObjectURL(draft[p])} target="_blank" rel="noreferrer">
                      Preview selected
                    </a>
                  )}

                  {hasExisting && !isRemoved && (
                    <Box sx={{ mt: 0.5 }}>
                      <a href={toUrl(existingArtworks[p], cdnBaseUrl)} target="_blank" rel="noreferrer">
                        View current
                      </a>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          position: 'sticky',
          bottom: 0,
          background: 'white',
          borderTop: '1px solid #eee',
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {removePlacements.length ? `${removePlacements.length} marked for deletion` : 'No deletions'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            save artwork
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

OnDemandArtworkModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  variantKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  placements: PropTypes.array.isRequired,
  existingArtworks: PropTypes.object,
  cdnBaseUrl: PropTypes.string,
  onApply: PropTypes.func.isRequired,
};

export default OnDemandArtworkModal;